import {
  Autocomplete,
  AutocompleteOption,
  ListItemContent,
  ListItemDecorator,
  Typography,
  Avatar,
  Box,
  Link,
} from "@mui/joy";
import React from "react";
import {
  PlacePrediction,
  usePostGooglePlacesAutocomplete,
} from "../api/use-post-google-places-autocomplete";
import { useSearchUsers, UserSearchResult } from "../api/use-search-users";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { useNavigate } from "react-router-dom";
import { IconButton } from "@mui/joy";

const INPUT_DEBOUNCE_TIME = 300;

type SearchResult =
  | { type: "place"; data: PlacePrediction }
  | { type: "person"; data: UserSearchResult };

type UniversalSearchProps = {
  onSelectPlace: (place: PlacePrediction | null) => void;
  sx: React.ComponentProps<typeof Autocomplete>["sx"];
  onClose?: () => void;
  searchTypes?: "places" | "people" | "both";
  hideSeeAllResults?: boolean;
  listboxZIndex?: number;
};

export const UniversalSearch = ({
  onSelectPlace,
  sx,
  onClose,
  searchTypes = "both",
  hideSeeAllResults = false,
  listboxZIndex,
}: UniversalSearchProps) => {
  const navigate = useNavigate();
  const timeoutIdRef = React.useRef<NodeJS.Timeout | null>(null);
  const [debouncedInput, setDebouncedInput] = React.useState("");
  const [inputValue, setInputValue] = React.useState("");

  const { data: placesData, isLoading: placesLoading } =
    usePostGooglePlacesAutocomplete({
      input: searchTypes !== "people" ? debouncedInput : "",
    });

  const { data: usersData, isLoading: usersLoading } = useSearchUsers(
    searchTypes !== "places" ? debouncedInput : "",
  );

  const isLoading = placesLoading || usersLoading;

  // Combine results into discriminated union
  const searchResults: SearchResult[] = React.useMemo(() => {
    const results: SearchResult[] = [];

    // Add place results
    if (placesData?.suggestions) {
      placesData.suggestions.forEach((suggestion) => {
        results.push({
          type: "place",
          data: suggestion.placePrediction,
        });
      });
    }

    // Add user results
    if (usersData) {
      usersData.forEach((user) => {
        if (!user.username) return;
        results.push({
          type: "person",
          data: user,
        });
      });
    }

    return results;
  }, [placesData, usersData]);

  const getOptionLabel = (option: SearchResult | string): string => {
    if (typeof option === "string") return option;
    if (option.type === "place") {
      return option.data.structuredFormat.mainText.text;
    }
    return option.data.full_name || option.data.username;
  };

  const handleChange = (
    event: React.SyntheticEvent,
    value: SearchResult | string | null,
  ) => {
    if (typeof value === "string" || !value) {
      onSelectPlace(null);
      return;
    }

    if (value.type === "place") {
      onSelectPlace(value.data);
      setInputValue("");
    } else if (value.type === "person") {
      navigate(`/profile/${value.data.id}`);
      setInputValue("");
    }
  };

  //TODO: fix the see all results not appearing correctly
  return (
    <Box sx={sx}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Autocomplete
          sx={{ flex: 1 }}
          freeSolo
          type="search"
          disableClearable
          placeholder={
            searchTypes === "places"
              ? "Search for places..."
              : searchTypes === "people"
                ? "Search for people..."
                : "Search for places or people..."
          }
          options={searchResults}
          value={undefined}
          inputValue={inputValue}
          getOptionLabel={getOptionLabel}
          groupBy={(option) => {
            if (typeof option === "string") return "";
            return option.type === "place" ? "Places" : "People";
          }}
          renderGroup={(params) => (
            <li key={params.group}>
              <Typography
                level="body-xs"
                sx={{
                  px: 2,
                  py: 1,
                  fontWeight: "bold",
                  textTransform: "uppercase",
                  color: "text.tertiary",
                  backgroundColor: "background.level1",
                }}
              >
                {params.group}
              </Typography>
              <ul style={{ padding: 0 }}>{params.children}</ul>
            </li>
          )}
          renderOption={(props, option) => {
            if (typeof option === "string") {
              return (
                <AutocompleteOption {...props}>{option}</AutocompleteOption>
              );
            }

            if (option.type === "place") {
              return (
                <AutocompleteOption {...props}>
                  <ListItemDecorator>
                    <LocationOnIcon fontSize="small" />
                  </ListItemDecorator>
                  <ListItemContent sx={{ fontSize: "sm" }}>
                    {option.data.structuredFormat.mainText.text}
                    <Typography level="body-xs" color="neutral">
                      {option.data.structuredFormat.secondaryText.text}
                    </Typography>
                  </ListItemContent>
                </AutocompleteOption>
              );
            }

            return (
              <AutocompleteOption {...props}>
                <ListItemDecorator>
                  <Avatar
                    src={option.data.avatar_url || undefined}
                    size="sm"
                    sx={{ width: 32, height: 32 }}
                  >
                    {option.data.username.charAt(0).toUpperCase()}
                  </Avatar>
                </ListItemDecorator>
                <ListItemContent sx={{ fontSize: "sm" }}>
                  {option.data.full_name || option.data.username}
                  <Typography level="body-xs" color="neutral">
                    @{option.data.username}
                  </Typography>
                </ListItemContent>
              </AutocompleteOption>
            );
          }}
          onInputChange={(event, value, reason) => {
            if (timeoutIdRef.current) {
              clearTimeout(timeoutIdRef.current);
            }

            setInputValue(value);

            if (reason === "clear") {
              setDebouncedInput("");
              return;
            }

            timeoutIdRef.current = setTimeout(() => {
              setDebouncedInput(value);
            }, INPUT_DEBOUNCE_TIME);
          }}
          onChange={handleChange}
          loading={isLoading}
          endDecorator={<SearchIcon />}
          slotProps={{
            listbox: {
              sx: {
                maxHeight: "400px",
                ...(listboxZIndex != null && { zIndex: listboxZIndex }),
              },
            },
          }}
        />
        {onClose && (
          <IconButton
            aria-label="Close search"
            size="sm"
            variant="plain"
            onClick={onClose}
            data-testid="mobile-search-close"
          >
            <CloseIcon />
          </IconButton>
        )}
      </Box>
      {!hideSeeAllResults && debouncedInput && searchResults.length > 0 && (
        <Box
          sx={{
            position: "absolute",
            bottom: -32,
            left: 0,
            right: 0,
            textAlign: "center",
            zIndex: 100,
          }}
        >
          <Link
            level="body-sm"
            onClick={() => {
              navigate(`/search?q=${encodeURIComponent(debouncedInput)}`);
              setInputValue("");
              setDebouncedInput("");
            }}
            sx={{ cursor: "pointer" }}
          >
            See all results
          </Link>
        </Box>
      )}
    </Box>
  );
};
